import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, Menu, MenuItem, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import useTheme from '~/theme/ThemeProvider';

const RotatingIcon = styled(ExpandMoreIcon, {
  shouldForwardProp: prop => prop !== 'open',
})<{ open?: boolean }>(({ open, theme }) => ({
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
}));

const LANGUAGES = [
  { code: 'en', label: 'common.lang.en' },
  { code: 'de', label: 'common.lang.de' },
];

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();

  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchor);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const closeMenu = () => setAnchor(null);
  const changeLanguage = (lang: string) => {
    closeMenu();
    setTimeout(() => void i18n.changeLanguage(lang), 125);
  };

  const activeLanguage = LANGUAGES.find(l => l.code === i18n.resolvedLanguage);
  const otherLanguages = LANGUAGES.filter(l => l.code !== i18n.resolvedLanguage);
  const orderedLanguages = activeLanguage ? [activeLanguage, ...otherLanguages] : otherLanguages;

  const appTheme = useTheme();
  const isMobileSize = useMediaQuery(appTheme.theme.breakpoints.only('xs'));

  return (
    <>
      <Button
        disableRipple
        variant="text"
        id="change-language-button"
        onClick={openMenu}
        endIcon={<RotatingIcon open={open} />}
        aria-haspopup="true"
        aria-controls={open ? 'change-language-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        sx={{
          height: 24,
          color: 'inherit',
          '&:focus-visible': {
            outline: '2px solid currentColor',
            outlineOffset: '2px',
          },
        }}
      >
        <Typography sx={{ textTransform: isMobileSize ? 'uppercase' : 'none' }}>
          {isMobileSize ? i18n.resolvedLanguage : <Trans i18nKey={`common.lang.${i18n.resolvedLanguage}`} />}
        </Typography>
      </Button>

      <Menu
        id="change-language-menu"
        anchorEl={anchor}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              padding: 0,
              width: 'auto',
              [appTheme.theme.breakpoints.down('md')]: {
                width: 'auto',
              },
            },
          },
          list: {
            sx: { padding: 0 },
            'aria-labelledby': 'change-language-button',
          },
        }}
      >
        {orderedLanguages.map(lang => {
          const selected = i18n.resolvedLanguage === lang.code;
          return (
            <MenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              selected={selected}
              sx={{
                padding: '8px 16px',
                backgroundColor: selected ? 'action.selected' : undefined,
              }}
            >
              <Typography>
                {t(lang.label)}
                {!selected && ` (${i18n.getFixedT(lang.code)(lang.label)})`}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
